import { errorCode } from './billingProfileAdapter';
import { TUNNEL_TIMEOUT_CODE } from './ledgerMovesAdapter';

// Features: M004-F0772
// Tests: T-M004-0372
// WebUI #245: a relay write can be applied by the Ledger while Core's answer is lost or late. Classify the failure so the page never
// shows a definite failure for an outcome it does not know. 'rejected' = a definite refusal (a 4xx with a Ledger/Core code);
// 'unknown' = the Ledger may have applied the command (a tunnel/node timeout, a Ledger outbox still in progress, 502/503/504, or no
// response at all). The caller re-reads and compares what it submitted; it never reports 'unknown' as a failure.

// Reuses the Ledger move adapter's tunnel-timeout code (Core #393) and the billing adapter's code extraction (string or { code }).
// Any *TIMEOUT code, and LEDGER_HA_OUTBOX_IN_PROGRESS, also mean the Ledger may still apply the command.
const OUTBOX_IN_PROGRESS = 'LEDGER_HA_OUTBOX_IN_PROGRESS';
const UNKNOWN_STATUSES = Object.freeze([502, 503, 504]);

export function classifyRelayOutcome(cause) {
  const response = cause?.response;
  if (!response) return 'unknown';
  const code = errorCode(cause);
  if (code === TUNNEL_TIMEOUT_CODE || code === OUTBOX_IN_PROGRESS || /TIMEOUT$/.test(code)) return 'unknown';
  if (UNKNOWN_STATUSES.includes(Number(response.status))) return 'unknown';
  return 'rejected';
}

// Decoded size of a base64 string (exact for padded base64, which is what the page sends).
const base64Bytes = (b64) => {
  const s = String(b64 || '');
  const pad = s.endsWith('==') ? 2 : (s.endsWith('=') ? 1 : 0);
  return Math.floor((s.length * 3) / 4) - pad;
};

// True when a fresh read of the billing profile shows every field the save SUBMITTED (and the logo action, when one was sent).
// Untouched fields are not compared: another user's change to them is not a failure of this save.
export function submittedFieldsMatch(billing, payload) {
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const profile = billing?.profile || {};
  const fieldsMatch = Object.entries(payload?.changes || {}).every(([name, value]) => same(profile[name], value));
  if (!fieldsMatch) return false;
  if (!payload?.logo) return true;
  if (payload.logo.action === 'remove') return !billing?.logo?.exists;
  return billing?.logo?.exists === true && billing.logo.bytes === base64Bytes(payload.logo.jpeg_base64);
}
